import { h, Component } from 'preact';
import { showModal } from 'utils/modal';
import Button from 'components/button/button';
import { assignMedia, upload } from 'utils/media';
import Store from 'store/store';
import { showUploadManager } from 'components/media/uploadManagerToast';
import s from 'components/media/cropper.sss';
import { getAllMedia } from 'utils/api';
import * as jsonPointer from 'utils/jsonPointer';

const handles = {
	topLeft: { x: 'left', y: 'top' },
	topRight: { x: 'right', y: 'top' },
	bottomLeft: { x: 'left', y: 'bottom' },
	bottomRight: { x: 'right', y: 'bottom' },
};

export default class Cropper extends Component {
	constructor(props) {
		super(props);
		this.cancel = this.cancel.bind(this);
		this.state = {
			canvasDrawn: false,
			zoom: 1,
			mouseOnHandle: null,
			mouseInFrame: false,
			transform: new DOMMatrix(),
			lastX: 0,
			lastY: 0,
			photo: null,
		};
		this.imageBounds = null;
		this.bounds = null;
		this.drawCanvas = this.drawCanvas.bind(this);
		this.drawFrame = this.drawFrame.bind(this);
		this.initCanvas = this.initCanvas.bind(this);
		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.snapSize = this.snapSize.bind(this);
		this.continue = this.continue.bind(this);
		this.savePhoto = this.savePhoto.bind(this);
	}

	cancel() {
		this.base.close('CANCEL');
	}

	// ---------------------------------------------------
	// API CALLS
	// ---------------------------------------------------

	dataURLtoBlob(dataURL) {
		const parts = dataURL.split(';base64,');
		const contentType = parts[0].split(':')[1];
		const raw = window.atob(parts[1]);
		const rawLength = raw.length;
		const uInt8Array = new Uint8Array(rawLength);

		for (let i = 0; i < rawLength; ++i) {
			uInt8Array[i] = raw.charCodeAt(i);
		}

		return new Blob([uInt8Array], { type: contentType });
	}

	async uploadAndAssign() {
		const blob = this.dataURLtoBlob(this.state.photo);
		const fileToReplace = await jsonPointer.get(Store.get().data, this.props.path);
		blob.name = fileToReplace ? this.props.file.name.replace('.webp', '-cropped.webp') : this.props.file.name;
		const opts = { onProcessed: async (response) => {
			await assignMedia(
				this.props.path,
				{
					...response,
					s3_url: response.url,
					movie: this.props.assetData?.type === 'movie' ? [this.props.assetData?.group] : [],
					section: this.props.assetData?.type === 'section' ? [this.props.assetData?.group] : []
				}
			);
		} };
		if (fileToReplace) {
			opts.replaceId = fileToReplace.id;
		}
		const uploadData = upload(blob, opts);
		this.base.close();
		const uploads = [uploadData];
		const mountPoint = document.querySelector('dialog#media-selector');
		showUploadManager(uploads, mountPoint);
		await Promise.allSettled(uploads.map(u => u.promise));
		Store.emit('UPDATE_MEDIA', { uploads });
		const media = await getAllMedia();
		const file = media.find(m => m.id === (uploadData.mediaId));
		assignMedia(this.props.path, file);
	}

	async continue() {
		await assignMedia(
			this.props.path,
			this.props.file
		);
		this.base.close();
	}


	// ---------------------------------------------------
	// MOUSE EVENT PROCESSING
	// ---------------------------------------------------


	adjustMousePosition(mouseX, mouseY) {
		const rect = this.$canvas.getBoundingClientRect();
		const scalingX = rect.width / this.$canvas.width;
		const scalingY = rect.height / this.$canvas.height;
		mouseX -= rect.left;
		mouseY -= rect.top;
		mouseX /= scalingX;
		mouseY /= scalingY;
		return { mouseXAdjusted: mouseX, mouseYAdjusted: mouseY, scaling: scalingX };
	}

	isMouseInFrame(mouseX, mouseY) {
		if (!this.bounds) return false;
		const { mouseXAdjusted, mouseYAdjusted } = this.adjustMousePosition(mouseX, mouseY);
		return (
			mouseXAdjusted >= this.bounds.left && mouseXAdjusted <= this.bounds.right &&
			mouseYAdjusted >= this.bounds.top && mouseYAdjusted <= this.bounds.bottom
		);
	}

	isMouseOnHandle(mouseX, mouseY) {
		if (!this.bounds) return null;
		const { mouseXAdjusted, mouseYAdjusted, scaling } = this.adjustMousePosition(mouseX, mouseY);
		const handleSize = 12 / scaling;
		for (const [key, handle] of Object.entries(handles)) {
			const x = this.bounds[handle.x];
			const y = this.bounds[handle.y];
			if (
				mouseXAdjusted >= x - handleSize / 2 && mouseXAdjusted <= x + handleSize / 2 &&
				mouseYAdjusted >= y - handleSize / 2 && mouseYAdjusted <= y + handleSize / 2
			) {
				return key;
			}
		}
		return null;
	}

	handlePointerDown(e) {
		// if not on handle, return
		if (!this.$canvas) return;
		const handle = this.isMouseOnHandle(e.clientX, e.clientY);
		// if no handle, check to see if inside bounds, if so, start drag to move
		if (!handle) {
			if (this.isMouseInFrame(e.clientX, e.clientY)) {
				this.setState({ mouseInFrame: true, lastX: e.clientX, lastY: e.clientY });
				document.addEventListener('pointermove', this.handlePointerMove);
				document.addEventListener('pointerup', this.handlePointerUp);
			}
			return;
		}
		this.setState({ mouseOnHandle: handle, lastX: e.clientX, lastY: e.clientY });
		document.addEventListener('pointermove', this.handlePointerMove);
		document.addEventListener('pointerup', this.handlePointerUp);
	}

	handlePointerMove(e) {
		if (this.state.mouseOnHandle) {
			const handle = this.state.mouseOnHandle;
			const handleBounds = handles[handle];
			let dx = e.clientX - this.state.lastX;
			let dy = e.clientY - this.state.lastY;

			if (handle === 'topLeft' || handle === 'bottomRight') {
				dy = dx * (this.height / this.width); // maintain aspect ratio
			} else {
				dy = dx * (this.height / this.width) * -1; // maintain aspect ratio
			}

			// keep within image
			if (this.bounds[handleBounds.x] + dx < this.imageBounds.left) return;
			if (this.bounds[handleBounds.x] + dx > this.imageBounds.right) return;
			if (this.bounds[handleBounds.y] + dy < this.imageBounds.top) return;
			if (this.bounds[handleBounds.y] + dy > this.imageBounds.bottom) return;

			// multiplier to account for difference in canvas size vs displayed size
			const canvasDiffMult = this.$canvas.getBoundingClientRect().width / this.$canvas.width;

			this.bounds[handleBounds.x] += dx / canvasDiffMult;
			this.bounds[handleBounds.y] += dy / canvasDiffMult;
		} else if (this.state.mouseInFrame) {
			// dragging to move the frame
			let dx = e.clientX - this.state.lastX;
			let dy = e.clientY - this.state.lastY;

			const canvasDiffMult = this.$canvas.getBoundingClientRect().width / this.$canvas.width;
			if (this.bounds.left + dx / canvasDiffMult < this.imageBounds.left) dx = 0;
			if (this.bounds.right + dx / canvasDiffMult > this.imageBounds.right) dx = 0;
			if (this.bounds.top + dy / canvasDiffMult < this.imageBounds.top) dy = 0;
			if (this.bounds.bottom + dy / canvasDiffMult > this.imageBounds.bottom) dy = 0;

			this.bounds.left += dx / canvasDiffMult;
			this.bounds.right += dx / canvasDiffMult;
			this.bounds.top += dy / canvasDiffMult;
			this.bounds.bottom += dy / canvasDiffMult;
		} else {
			return;
		}

		this.setState({
			lastX: e.clientX,
			lastY: e.clientY,
		});

		this.drawCanvas();
	}

	handlePointerUp() {
		this.setState({ mouseOnHandle: null, mouseInFrame: false });
		document.removeEventListener('pointermove', this.handlePointerMove);
		document.removeEventListener('pointerup', this.handlePointerUp);
	}


	// ---------------------------------------------------
	// CANVAS ANIMATION
	// ---------------------------------------------------


	initCanvas() {
		this.canvasContext = document.getElementById('cropperCanvas')?.getContext('2d');
		if (this.props.file?.s3_url) {
			this.img = new Image();
			this.img.crossOrigin = 'anonymous';
			this.img.src = this.props.file.s3_url;
			this.img.onload = this.drawCanvas;
			this.setState({ canvasDrawn: true });
		}
	}

	drawCanvas() {
		if (this.unmounted) {
			return;
		}
		if (!this.canvasContext) this.canvasContext = document.getElementById('cropperCanvas')?.getContext('2d');
		if (!this.$canvas) this.$canvas = document.getElementById('cropperCanvas');
		this.canvasContext.clearRect(0, 0, this.$canvas.width, this.$canvas.height);
		this.canvasContext.save();
		this.canvasContext.setTransform(this.state.transform);
		const startingPointX = this.$canvas.width / 2 - (this.img.width / 2 * this.state.zoom);
		const startingPointY = this.$canvas.height / 2 - (this.img.height / 2 * this.state.zoom);
		this.canvasContext.drawImage(
			this.img,
			startingPointX, // starting point x
			startingPointY, // starting point y
			this.img.width * this.state.zoom, // width to draw
			this.img.height * this.state.zoom, // height to draw
		);
		this.imageBounds = {
			left: startingPointX,
			right: startingPointX + this.img.width * this.state.zoom,
			top: startingPointY,
			bottom: startingPointY + this.img.height * this.state.zoom,
		};
		this.canvasContext.setTransform(new DOMMatrix()); // reset transform for drawing the frame
		if (!this.width && !this.height) {
			// if frame would be larger than canvas, divide size of frame and store the multiplier
			const width = this.props.width ?? this.img.width;
			const height = this.props.height ?? (this.props.aspectRatio ? width / this.props.aspectRatio : this.img.height);
			if (width > this.$canvas.width || height > this.$canvas.height) {
				const widthRatio = width / this.$canvas.width;
				const heightRatio = height / this.$canvas.height;
				const widthMultiplier = Math.max(widthRatio, heightRatio);
				const heightMultiplier = Math.max(widthRatio, heightRatio);
				this.width = width / widthMultiplier;
				this.height = height / heightMultiplier;
			} else {
				this.width = width;
				this.height = height;
			}
			this.snapSize();
		}
		if (!this.state.saving) {
			this.drawFrame();
			this.drawHandles();
		}
		this.canvasContext.restore();
		cancelAnimationFrame(this.timer);
		this.timer = requestAnimationFrame(this.drawCanvas);
	}

	drawFrame() {
		this.canvasContext.fillStyle = 'rgba(0, 0, 0, 0.5)'; // semi-opaque grey
		if (!this.bounds) {
			const currentImgWidth = this.img.width * this.state.zoom;
			const currentImgHeight = this.img.height * this.state.zoom;

			const aspectRatio = this.width / this.height;

			let k;
			if (currentImgWidth / currentImgHeight > aspectRatio) {
				k = currentImgHeight / this.height;
			} else {
				k = currentImgWidth / this.width;
			}
			const frameWidth = this.width * k;
			const frameHeight = this.height * k;

			const leftBound = (this.$canvas.width - frameWidth) / 2;

			const topBound = (this.$canvas.height - frameHeight) / 2;

			this.bounds = {
				left: leftBound,
				right: this.$canvas.width - leftBound,
				top: topBound,
				bottom: this.$canvas.height - topBound,
			};
		}
		this.canvasContext.fillRect( // left side rectangle
			0, // x
			0, // y
			Math.round(this.bounds.left), // width
			this.$canvas.height // height
		);
		this.canvasContext.fillRect( // right side rectangle
			Math.round(this.bounds.right), // x
			0, // y
			this.$canvas.width, // width
			this.$canvas.height // height
		);
		this.canvasContext.fillRect( // top side rectangle
			Math.round(this.bounds.left), // x
			0, // y
			Math.round(this.bounds.right) - Math.round(this.bounds.left), // width
			this.bounds.top // height
		);
		this.canvasContext.fillRect( // bottom side rectangle
			Math.round(this.bounds.left), // x
			this.bounds.bottom, // y
			Math.round(this.bounds.right) - Math.round(this.bounds.left), // width
			this.$canvas.height // height
		);
		this.canvasContext.strokeStyle = '#6721CF';
		this.canvasContext.lineWidth = 1;
		this.canvasContext.strokeRect(this.bounds.left, this.bounds.top, this.bounds.right - this.bounds.left, this.bounds.bottom - this.bounds.top);
	}

	drawHandles() {
		if (!this.bounds || !this.canvasContext) return;
		const handleSize = 8;
		this.canvasContext.strokeStyle = '#6721CF';
		this.canvasContext.lineWidth = 1;
		Object.values(handles).forEach(handle => this.drawHandle({ x: this.bounds[handle.x], y: this.bounds[handle.y] }, handleSize));
	}

	drawHandle({ x, y }, handleSize) {
		this.canvasContext.fillStyle = 'white';
		this.canvasContext.fillRect(x - 4, y - 4, handleSize, handleSize);
		this.canvasContext.strokeRect(x - 4, y - 4, handleSize, handleSize);
	}


	// ---------------------------------------------------
	// POSITIONING
	// ---------------------------------------------------

	snapSize() {
		let zoom = 1;
		const imgWidthDiff = this.img.width - this.$canvas.width;
		const imgHeightDiff = this.img.height - this.$canvas.height;
		if (imgWidthDiff > 0 || imgHeightDiff > 0) {
			if (this.img.width > this.img.height) {
				zoom = this.$canvas.width / this.img.width;
			} else {
				zoom = this.$canvas.height / this.img.height;
			}
		}
		this.setState({ transform: new DOMMatrix(), zoom });
	}

	// ---------------------------------------------------
	// SAVING
	// ---------------------------------------------------

	savePhoto() {
		if (!this.$canvas) return;
		this.setState({ saving: true });
		setTimeout(() => {
			const croppedCanvas = document.createElement('canvas');
			croppedCanvas.width = this.props.width ?? this.bounds.right - this.bounds.left;
			croppedCanvas.height = this.props.height ?? this.bounds.bottom - this.bounds.top;
			const croppedContext = croppedCanvas.getContext('2d');

			croppedContext.drawImage(
				this.$canvas,
				this.bounds.left, // source x
				this.bounds.top, // source y
				this.bounds.right - this.bounds.left, // source width
				this.bounds.bottom - this.bounds.top, // source height
				0, // destination x
				0, // destination y
				croppedCanvas.width, // destination width
				croppedCanvas.height // destination height
			);

			const dataUrl = croppedCanvas.toDataURL('image/webp');
			this.setState({ photo: dataUrl, saving: false });
		}, 100); // give time for saving state to update before redrawing without frame
	}

	// ---------------------------------------------------
	// RENDERING
	// ---------------------------------------------------


	render() {
		if (!this.state.canvasDrawn) {
			this.initCanvas();
		}
		return (
			<dialog class={s.cropper} id="cropperModal">
				<Button class={s.close} icon="cancel" iconSize="large" onClick={this.cancel} />
				<div class={s.step}>
					<h2>Crop image</h2>
					<canvas
						id="cropperCanvas"
						width="1000"
						height="1000"
						ref={($ref) => this.$canvas = $ref}
						onPointerDown={this.handlePointerDown}
					/>
					<div class={s.buttonRow}>
						<Button onClick={this.cancel}>Cancel</Button>
						<Button
							primary
							onClick={() => {
								this.savePhoto();
								setTimeout(() => {
									this.uploadAndAssign();
								}, 100);
							}}
						>
								Use Image
						</Button>
					</div>
				</div>
			</dialog>
		);
	}
}

export async function showCropper(options) {
	const data = await showModal(Cropper, options);
	if (!data || data === 'CANCEL') return data || null;
	try {
		return JSON.parse(data);
	} catch {}
	return null;
}